import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  Search,
  Filter,
  AlertCircle,
  Clock,
  CheckCircle2,
  XCircle,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  LoaderPinwheel,
  Headphones,
  AlertTriangle,
  User,
  Calendar,
} from "lucide-react";
import Navbar from "../components/ui/Navbar";
import { hasPermission } from "../utils/permissions";
import SoporteFormModal from "../components/modals/SoporteFormModal";
import SoporteDetailModal from "../components/modals/SoporteDetailModal";
import SelectSimple from "../components/ui/SelectSimple";
import PageSizeSelect from "../components/ui/PageSizeSelect";

const API = process.env.REACT_APP_API_URL;

const PRIORIDAD_COLOR = {
  Baja: "bg-green-100 text-green-700 border-green-200",
  Media: "bg-yellow-100 text-yellow-700 border-yellow-200",
  Alta: "bg-orange-100 text-orange-700 border-orange-200",
  Crítica: "bg-red-100 text-red-700 border-red-200",
};

const ESTADO_COLOR = {
  Nuevo: "bg-blue-100 text-blue-700",
  "En revisión": "bg-purple-100 text-purple-700",
  "En atención": "bg-indigo-100 text-indigo-700",
  "En espera cliente": "bg-yellow-100 text-yellow-700",
  Resuelto: "bg-green-100 text-green-700",
  Cerrado: "bg-gray-100 text-gray-600",
  Cancelado: "bg-red-100 text-red-500",
};

const ESTADO_ICON = {
  Nuevo: <AlertCircle className="h-3.5 w-3.5" />,
  "En revisión": <Clock className="h-3.5 w-3.5" />,
  "En atención": <RefreshCw className="h-3.5 w-3.5" />,
  "En espera cliente": <Clock className="h-3.5 w-3.5" />,
  Resuelto: <CheckCircle2 className="h-3.5 w-3.5" />,
  Cerrado: <CheckCircle2 className="h-3.5 w-3.5" />,
  Cancelado: <XCircle className="h-3.5 w-3.5" />,
};

export default function SoportesPage() {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const [tickets, setTickets] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);

  const [filtros, setFiltros] = useState({
    buscar: "",
    estado: "",
    prioridad: "",
    tipo: "",
  });
  const [showFiltros, setShowFiltros] = useState(false);

  const [limit, setLimit] = useState(15);

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit, ...filtros };
      Object.keys(params).forEach((k) => {
        if (!params[k]) delete params[k];
      });
      const { data } = await axios.get(`${API}/soporte`, { headers, params });
      setTickets(data.tickets || []);
      setTotal(data.total || 0);
    } catch (err) {
      toast.error("Error al cargar tickets");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, [page, limit, filtros, token]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const handleFiltro = (e) => {
    setFiltros((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setPage(1);
  };

  const handleOpenDetail = (ticket) => {
    setSelectedTicket(ticket);
    setShowDetail(true);
  };

  const handleCreado = () => {
    setShowForm(false);
    fetchTickets();
    toast.success("Ticket creado correctamente");
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="container mx-auto px-4 py-6 pt-20 md:pt-28">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-blue-600 rounded-xl">
              <Headphones className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Soporte a Equipos
              </h1>
              <p className="text-sm text-gray-500">
                Gestión de tickets de soporte técnico · {total} ticket
                {total !== 1 ? "s" : ""}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {hasPermission("soporte.reportes") && (
              <button
                onClick={() => navigate("/soporte/metricas")}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-100 transition"
              >
                <AlertTriangle className="h-4 w-4" /> Métricas
              </button>
            )}
            {hasPermission("soporte.crear") && (
              <button
                onClick={() => setShowForm(true)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition shadow"
              >
                <Plus className="h-4 w-4" /> Nuevo ticket
              </button>
            )}
          </div>
        </div>

        {/* Barra búsqueda y filtros */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                name="buscar"
                value={filtros.buscar}
                onChange={handleFiltro}
                placeholder="Buscar por código, asunto, solicitante..."
                className="pl-10 pr-4 py-2 border border-gray-200 rounded-lg w-full text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <button
              onClick={() => setShowFiltros(!showFiltros)}
              className={`flex items-center gap-2 px-4 py-2 border rounded-lg text-sm transition ${showFiltros ? "bg-blue-50 border-blue-300 text-blue-700" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}
            >
              <Filter className="h-4 w-4" /> Filtros
            </button>
          </div>

          {showFiltros && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-3 pt-3 border-t border-gray-100">
              <SelectSimple
                name="estado"
                value={filtros.estado}
                onChange={handleFiltro}
                placeholder="Todos los estados"
                options={[
                  "Nuevo",
                  "En revisión",
                  "En atención",
                  "En espera cliente",
                  "Resuelto",
                  "Cerrado",
                  "Cancelado",
                ]}
              />
              <SelectSimple
                name="prioridad"
                value={filtros.prioridad}
                onChange={handleFiltro}
                placeholder="Todas las prioridades"
                options={["Baja", "Media", "Alta", "Crítica"]}
              />
              <SelectSimple
                name="tipo"
                value={filtros.tipo}
                onChange={handleFiltro}
                placeholder="Todos los tipos"
                options={[
                  "Incidencia",
                  "Consulta técnica",
                  "Capacitación",
                  "Solicitud administrativa",
                  "Otro",
                ]}
              />
            </div>
          )}
        </div>

        {/* Tabla */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="flex items-center justify-end px-4 py-2 border-b border-gray-100">
            <PageSizeSelect
              value={limit}
              onChange={(size) => {
                setLimit(size);
                setPage(1);
              }}
            />
          </div>
          {loading ? (
            <div className="flex items-center gap-2 rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-sm text-blue-700">
              <LoaderPinwheel className="h-4 w-4 animate-spin text-blue-600" />
              Cargando datos, por favor espera...
            </div>
          ) : tickets.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              <Headphones className="h-12 w-12 mb-3 opacity-30" />
              <p className="text-lg font-medium">No hay tickets</p>
              <p className="text-sm">
                {Object.values(filtros).some(Boolean)
                  ? "Prueba ajustando los filtros"
                  : "Crea el primer ticket de soporte"}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600">
                      Código
                    </th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600">
                      Asunto
                    </th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600">
                      Cliente
                    </th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600">
                      Prioridad
                    </th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600">
                      Estado
                    </th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600">
                      Responsable
                    </th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600">
                      Fecha
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {tickets.map((t) => (
                    <tr
                      key={t._id}
                      onClick={() => handleOpenDetail(t)}
                      className="hover:bg-blue-50 cursor-pointer transition-colors"
                    >
                      <td className="px-4 py-3 font-mono text-blue-700 font-semibold text-xs">
                        {t.codigo}
                      </td>
                      <td className="px-4 py-3 max-w-xs">
                        <p className="font-medium text-gray-800 truncate">
                          {t.asunto}
                        </p>
                        <p className="text-gray-400 text-xs truncate">
                          {t.tipo}
                        </p>
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        {t.cliente?.name || "—"}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${PRIORIDAD_COLOR[t.prioridad]}`}
                        >
                          <AlertCircle className="h-3 w-3" /> {t.prioridad}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${ESTADO_COLOR[t.estado]}`}
                        >
                          {ESTADO_ICON[t.estado]} {t.estado}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        <div className="flex items-center gap-1">
                          <User className="h-3.5 w-3.5 text-gray-400" />
                          <span className="text-xs">
                            {t.responsable?.name || "Sin asignar"}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs">
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3.5 w-3.5" />
                          {new Date(t.createdAt).toLocaleDateString("es-CO")}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Paginación */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 bg-gray-50">
              <p className="text-sm text-gray-500">
                {(page - 1) * limit + 1}–{Math.min(page * limit, total)} de{" "}
                {total}
              </p>
              <div className="flex items-center gap-1">
                <button
                  disabled={page === 1}
                  onClick={() => setPage((p) => p - 1)}
                  className="p-1.5 rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-white transition"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="px-3 py-1 text-sm font-medium text-gray-700">
                  {page} / {totalPages}
                </span>
                <button
                  disabled={page === totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  className="p-1.5 rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-white transition"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {showForm && (
        <SoporteFormModal
          onClose={() => setShowForm(false)}
          onCreado={handleCreado}
        />
      )}

      {showDetail && selectedTicket && (
        <SoporteDetailModal
          ticketId={selectedTicket._id}
          onClose={() => {
            setShowDetail(false);
            fetchTickets();
          }}
        />
      )}
    </div>
  );
}
